import React, { useEffect, useState } from "react";
import { Pagination, Switch } from "@mui/material";
import styles from "./subExcrowDetails.module.css";
import SubEscrowModal from "./SubEscrowModal";
import { useAllProduct } from "../../../../api-context/commonApiContext";
import config from "../../../../service/config";
import FullScreenLoader from "../../../../reusable/loader/FullScreenLoader";

const SubEscrowDetails = ({ selectedEscrow, subEscrowAllowed }) => {
  const { escrowEntries, setLoading, loading, clientId, fetchEscrowEntries } =
    useAllProduct();

  const [currentPage, setCurrentPage] = useState(1);
  const [escrowList, setEscrowList] = useState([]);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubEscrowAllowed, setIsSubEscrowAllowed] =
    useState(subEscrowAllowed);

  useEffect(() => {
    if (!clientId) return;

    const timeout = setTimeout(() => {
      setLoading(true);
      fetch(`${config.node_api_endpoint}/api/escrow-subescrow-toggle`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({
          client_id: clientId,
          company_name: selectedEscrow.registered_company_name,
          isSubEscrowAllowed: isSubEscrowAllowed,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          if (!result.success) {
            console.error(
              "Failed to update sub escrow toggle:",
              result.message
            );
          } else {
            fetchEscrowEntries();
          }
        })
        .catch((error) => {
          console.error("Error updating sub escrow toggle:", error);
        })
        .finally(() => {
          setLoading(false); // ✅ End loader
        });
    }, 500);

    return () => clearTimeout(timeout);
  }, [isSubEscrowAllowed, clientId, selectedEscrow?.main_escrow_id]);

  // Reset page when escrow selection changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEscrow]);

  // Filter escrow entries for current selection
  useEffect(() => {
    if (!escrowEntries) return;
    const filterTableData = escrowEntries.filter(
      (data) => data.main_escrow === selectedEscrow.main_escrow_id
    );
    setEscrowList(filterTableData);
  }, [selectedEscrow, escrowEntries]);

  // Prepare flat list of sub escrows
  const allSubEscrows =
    escrowList?.flatMap((entry) =>
      (entry.sub_escrow || []).map((report) => ({
        ...report,
        main_escrow: entry.main_escrow,
        company_name: entry.company_name,
      }))
    ) || [];

  const filteredSubEscrows = allSubEscrows.filter((report) => {
    const query = searchQuery.toLowerCase();
    return (
      report?.id?.toLowerCase().includes(query) ||
      report?.status?.toLowerCase().includes(query) ||
      report?.created_at?.toLowerCase().includes(query)
    );
  });

  const paginatedReports = filteredSubEscrows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const disableSubescrow = (sub_escrow_id) => {
    const main_escrow_id = selectedEscrow.main_escrow_id;
    setLoading(true);
    fetch(`${config.node_api_endpoint}/api/escrow-details/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({
        main_escrow_id,
        sub_escrow_id,
        client_id: clientId,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          alert("Sub escrow disabled");
          fetchEscrowEntries(); // ✅ Refetch after disabling
        } else {
          alert(result.message);
        }
      })
      .catch((error) => {
        console.error("Failed to disable sub escrow:", error);
      })
      .finally(() => {
        setLoading(false); // ✅ End loader
      });
  };
  
  return (
    <>
      <FullScreenLoader spinning={loading} />
      <div className="d-flex justify-content-between align-items-center">
        <div>
          Allow user to create sub escrow{" "}
          <Switch
            checked={isSubEscrowAllowed}
            onChange={() => setIsSubEscrowAllowed((prev) => !prev)}
            color="primary"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#ffffff" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#2348AB",
              },
            }}
          />
        </div>

        <div
          className={`${styles.bulkPayoutButton} d-flex justify-content-center align-items-center p-2 gap-2`}
          onClick={() => {
            setShowModal(true);
          }}
        >
          <img
            className={`${styles.plusCircleIcon} pe-1`}
            alt=""
            src="/pluscircle.svg"
          />
          Add Sub Escrow
        </div>
      </div>

      <div className={`${styles.root} mt-2`}>
        <section className={styles.transactionDataTable}>
          <div className={styles.top}>
            <div className={styles.reportHeader}>
              <h3 className={styles.latestAccountTransaction}>
                Sub Escrow List
              </h3>
              <div className={styles.filter}>
                <div className={styles.textField}>
                  <div className={styles.content}>
                    <img
                      className={styles.leadingIcon}
                      alt=""
                      src="/leadingicon.svg"
                    />
                    <input
                      className={styles.filterLabel}
                      placeholder="Search..."
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
                <div
                  className={styles.reLoadTable}
                  onClick={() => fetchEscrowEntries()}
                >
                  <img
                    className={styles.reloadIcon}
                    loading="lazy"
                    alt=""
                    src="/reload.svg"
                  />
                </div>
              </div>
            </div>
            <div className={styles.headerSeparator} />
          </div>

          <div className={styles.reportTable}>
            <div className={styles.tableHeader}>
              <div className={styles.cell}>CREATED AT</div>
              <div className={styles.cell}>SUB ESCROW ID</div>
              <div className={styles.cell}>STATUS</div>
              <div className={styles.cell}>ACTION</div>
            </div>

            {paginatedReports.map((report, index) => (
              <div className={styles.tableRow} key={index}>
                <div className={styles.cell}>{report.created_at}</div>
                <div className={styles.cell}>{report.id}</div>
                <div className={styles.cell}>{report.status}</div>
                <div className={styles.cell}>
                  {selectedEscrow.sub_escrow_id !== report.id && (
                    <button
                      className={styles.actionButton}
                      onClick={() => disableSubescrow(report.id)}
                      disabled={report.status === "In-Active"}
                    >
                      {report.status === "In-Active" ? (
                        <span></span>
                      ) : (
                        <span>Disable</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-end mt-3">
            <Pagination
              count={Math.ceil(filteredSubEscrows.length / itemsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </div>

          {showModal && (
            <SubEscrowModal
              page="view"
              selectedEscrow={selectedEscrow}
              onClose={() => setShowModal(false)}
            />
          )}
        </section>
      </div>
    </>
  );
};

export default SubEscrowDetails;
